/**
 * news-neo.js
 *
 * Renders `contentBuilderNeoJson` (new AICO format). news.js delegates here
 * when `window.NewsNeo.hasContent(data)` is true; otherwise its legacy
 * `contentBuilder` path runs unchanged.
 *
 * Public API (window.NewsNeo):
 *   - hasContent(data)       → boolean
 *   - render(data)           → HTML string
 *   - hydrate(root)          → wire slideshows, lightbox, slick albums
 *   - registerType(type, fn) → add or override an element renderer
 */
(function (window) {
  'use strict';

  // Forward styles.desktop from the JSON to the DOM
  var USE_API_INLINE_STYLES = true;

  // ============================================================
  // Detection
  // ============================================================

  function hasNeoContent(data) {
    if (!data || typeof data !== 'object') return false;
    var neo = data.contentBuilderNeoJson;
    if (!neo || typeof neo !== 'object') return false;
    if (!Array.isArray(neo.sections) || neo.sections.length === 0) return false;
    return true;
  }

  // ============================================================
  // Style helpers (used only when USE_API_INLINE_STYLES is true)
  // ============================================================

  function camelToKebab(s) {
    return s.replace(/([A-Z])/g, '-$1').toLowerCase();
  }

  // Expand `padding`/`margin` shorthand into longhands when both are present, so longhands always win.
  function normalizeShorthands(styleObj) {
    var out = {};
    for (var k in styleObj) {
      if (Object.prototype.hasOwnProperty.call(styleObj, k)) out[k] = styleObj[k];
    }
    var sides = ['Top', 'Right', 'Bottom', 'Left'];
    ['padding', 'margin'].forEach(function (base) {
      var hasIndividual = sides.some(function (s) { return (base + s) in out; });
      if (!hasIndividual || !(base in out)) return;
      var parts = String(out[base]).trim().split(/\s+/);
      var top = parts[0];
      var right = parts[1] !== undefined ? parts[1] : top;
      var bottom = parts[2] !== undefined ? parts[2] : top;
      var left = parts[3] !== undefined ? parts[3] : right;
      var fill = { Top: top, Right: right, Bottom: bottom, Left: left };
      sides.forEach(function (s) { if (!(base + s in out)) out[base + s] = fill[s]; });
      delete out[base];
    });
    return out;
  }

  function stylesToInline(styleObj) {
    if (!styleObj || typeof styleObj !== 'object' || Array.isArray(styleObj)) return '';
    var normalized = normalizeShorthands(styleObj);
    var out = [];
    for (var key in normalized) {
      if (!Object.prototype.hasOwnProperty.call(normalized, key)) continue;
      var val = normalized[key];
      if (val === null || val === undefined || val === '') continue;
      out.push(camelToKebab(key) + ':' + val);
    }
    return out.join(';');
  }

  function pickStyles(node, breakpoint) {
    if (!node || !node.styles) return {};
    var s = node.styles[breakpoint || 'desktop'];
    if (!s || Array.isArray(s)) return {};
    return s;
  }

  // Quote-only escape for style="..." payloads. Deliberately NOT escapeAttr:
  // that also encodes `&`, which would break legitimate values like url(...&...).
  function escapeStyleValue(s) {
    if (s == null) return '';
    return String(s).replace(/"/g, '&quot;');
  }

  function inlineStyleAttr(node) {
    if (!USE_API_INLINE_STYLES) return '';
    var s = stylesToInline(pickStyles(node));
    return s ? ' style="' + escapeStyleValue(s) + '"' : '';
  }

  // Same as inlineStyleAttr but without the keys named in `omit` (a lookup object).
  function inlineStyleAttrExcept(node, omit) {
    if (!USE_API_INLINE_STYLES) return '';
    var src = pickStyles(node);
    var kept = {};
    for (var k in src) {
      if (Object.prototype.hasOwnProperty.call(src, k) && !omit[k]) kept[k] = src[k];
    }
    var s = stylesToInline(kept);
    return s ? ' style="' + escapeStyleValue(s) + '"' : '';
  }

  // Single CSS value for hand-built declarations (colours). Anything that could
  // close the declaration, the rule or the <style> element is dropped.
  function cssValue(v) {
    if (v == null) return '';
    return String(v).replace(/[;{}<>"'\\]/g, '').trim();
  }

  // Counter for per-element scoped <style> blocks (button hover colour).
  var scopedStyleSeq = 0;

  // Wrap `inner` in an <a> when the element carries a link URL.
  function wrapLink(inner, content) {
    var href = normalizeUrl(content.linkUrl);
    if (!href) return inner;
    var target = content.openInNewTab === true ? ' target="_blank" rel="noopener"' : '';
    return '<a href="' + escapeAttr(href) + '"' + target + '>' + inner + '</a>';
  }

  // Strip a single wrapping <p> so heading text from the rich-text editor isn't invalid <hN><p>.
  function stripOuterParagraph(html) {
    if (!html) return '';
    return String(html).replace(/^\s*<p[^>]*>([\s\S]*?)<\/p>\s*$/i, '$1');
  }

  function escapeAttr(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Pick the active translation: window.current_locale_code if matched, else first available.
  function pickTranslation(translations) {
    if (!translations || typeof translations !== 'object') return {};
    var loc = (typeof window !== 'undefined' && window.current_locale_code) ? window.current_locale_code : null;
    if (loc && translations[loc]) return translations[loc];
    for (var k in translations) {
      if (Object.prototype.hasOwnProperty.call(translations, k)) return translations[k] || {};
    }
    return {};
  }

  // Prefix bare domains with https:// so they don't get treated as relative paths.
  function normalizeUrl(url) {
    if (!url) return '';
    var s = String(url).trim();
    if (!s) return '';
    // Scheme test runs on a probe with control chars/spaces removed, because
    // browsers strip those when resolving (e.g. "java\tscript:"). The probe is
    // only used for this test - everything below still works on the original `s`.
    var probe = s.replace(/[\x00-\x20]+/g, '').toLowerCase();
    if (probe.indexOf('javascript:') === 0) return '';
    if (probe.indexOf('vbscript:') === 0) return '';
    if (probe.indexOf('data:') === 0 && probe.indexOf('data:image/') !== 0) return '';
    if (/^([a-z][a-z0-9+.\-]*:|\/\/|\/|#)/i.test(s)) return s;
    if (/^[^\s\/?]+\.[a-z]{2,}/i.test(s)) return 'https://' + s;
    return s;
  }

  // Element renderers return inner HTML only; row/column wrapping lives in
  // renderSection. The `columns` type is handled there, not here.
  var renderers = {};

  // The native text marks of an imported legacy news text (aico news import
  // --native-text-settings body-text-spans, font-runs, regular-paragraphs, bold-text-size), drawn as the markup they stand for:
  //   <small data-run="body">: the words of a span the theme draws as body text (its span
  //     rules give them the body size, face, colour and line height), written back as that span,
  //     and "body-regular" as that span with the regular weight;
  //   <small data-run="font"> / "font-1" to "font-7": a legacy font tag, plain or of that size,
  //     which the theme draws by its font rules, written back as that tag.
  //   The tags are rewritten in the string, before the browser parses it, so the words keep the
  //   place, nesting and line breaks the legacy tag gave them;
  //   <p data-run="regular">: a paragraph in the regular weight (inside a heading), as the legacy
  //     paragraph stated it;
  //   <strong data-size="12px"> (or <b>): the bold tag in that pixel size, 8px to 40px, as the
  //     legacy tag stated it (a theme rule that forces a size still wins over it).
  // A text without a mark is returned as it came, so it is drawn exactly as before.
  var TEXT_RUN_MARKS = { 'body': ['<span>', '</span>'], 'body-regular': ['<span style="font-weight:normal">', '</span>'], 'font': ['<font>', '</font>'] };
  for (var fontSize = 1; fontSize <= 7; fontSize++) TEXT_RUN_MARKS['font-' + fontSize] = ['<font size="' + fontSize + '">', '</font>'];
  var BOLD_TEXT_SIZE = /^(?:[89]|[1-3]\d|40)px$/;
  function withNativeTextMarks(html) {
    var s = String(html);
    if (s.indexOf(' data-run="') === -1 && !/<(?:strong|b) data-size="/i.test(s)) return html;
    var open = [];
    return s.replace(/<(\/?)(small|strong|b|p)\b([^>]*)>/gi, function (tag, close, name, attrs) {
      name = name.toLowerCase();
      if (name === 'p') return !close && attrs === ' data-run="regular"' ? '<p style="font-weight:400">' : tag;
      if (name !== 'small') {
        if (close) return tag;
        var size = attrs.match(/^ data-size="([^"]*)"$/);
        return size && BOLD_TEXT_SIZE.test(size[1]) ? '<' + name + ' style="font-size:' + size[1] + '">' : tag;
      }
      if (close) { var closing = open.pop(); return typeof closing === 'string' ? closing : tag; }
      var mark = attrs.match(/^ data-run="([a-z0-9-]+)"$/);
      var run = mark && Object.prototype.hasOwnProperty.call(TEXT_RUN_MARKS, mark[1]) ? TEXT_RUN_MARKS[mark[1]] : null;
      open.push(run ? run[1] : false);
      return run ? run[0] : tag;
    });
  }

  renderers.heading = function (el) {
    var content = el.content || {};
    var level = content.level || 'h2';
    if (!/^h[1-6]$/i.test(level)) level = 'h2';
    var text = withNativeTextMarks(stripOuterParagraph(content.text != null ? content.text : ''));
    // content.textStyle 'body' (news import --native-text-settings=heading-body-text): the words
    // are drawn as body text inside the heading's own box, which keeps its level, margins and
    // line pitch. The legacy markup was one regular-weight span, and this theme sizes every span
    // of the article body, so the words go back into a span of their own (news.css gives it the
    // regular weight). A heading without the setting is drawn exactly as before.
    if (content.textStyle === 'body' && text !== '') {
      text = '<span class="content-type-neo-heading-body-text">' + text + '</span>';
    }
    return '<div class="content-type-title content-type-neo-heading">' +
      '<' + level + inlineStyleAttr(el) + '>' + text + '</' + level + '>' +
      '</div>';
  };

  // A CSS length, as the native text settings carry it (the server renderer's pattern).
  var SETTING_LENGTH = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)(?:px|pt|cm|mm|in|pc|em|rem|%)?$/;

  // content.paragraphSpacing {top, bottom} (news import --native-text-settings=paragraph-spacing):
  // the vertical margins of every bare paragraph of the text, as the server renderer draws them.
  // A paragraph with an attribute of its own keeps what it says.
  function withParagraphSpacing(text, spacing) {
    if (!spacing || typeof spacing !== 'object') return text;
    var top = typeof spacing.top === 'string' ? spacing.top.trim() : '';
    var bottom = typeof spacing.bottom === 'string' ? spacing.bottom.trim() : '';
    if (!SETTING_LENGTH.test(top) || !SETTING_LENGTH.test(bottom)) return text;
    // lineHeight (news import --native-text-settings=paragraph-line-height): the line height the
    // legacy paragraph stated. It beats the theme's paragraph line height on a wide screen only;
    // the narrow-screen rule of news.css still forces its own, as it did on the legacy paragraph.
    var lineHeight = typeof spacing.lineHeight === 'string' ? spacing.lineHeight.trim().toLowerCase() : '';
    var extra = TIER_VALUE.test(lineHeight) ? ';line-height:' + lineHeight : '';
    return String(text).replace(/<p>/gi, '<p style="margin-top:' + top + ';margin-bottom:' + bottom + extra + '">');
  }

  // The narrower viewport tiers of the builder (styles.tablet, styles.mobile), each inheriting the
  // one above, on the same widths as the server renderer: tablet 768-1023px, mobile up to 767px.
  // Only the line height is drawn (news import --native-text-settings=line-height writes it for a
  // text element whose paragraph stated its own line height, which this theme forces to 22px on a
  // narrow screen). The rule is scoped by the element id and beats the desktop value inline.
  var TIER_MEDIA = { tablet: '(min-width:768px) and (max-width:1023px)', mobile: '(max-width:767px)' };
  var TIER_PROPERTIES = { lineHeight: 'line-height' };
  var TIER_VALUE = /^(?:normal|(?:\d+(?:\.\d+)?|\.\d+)(?:px|pt|em|rem|%)?)$/;

  function tierStyle(el, scope) {
    var styles = el && el.styles;
    if (!styles || typeof styles !== 'object') return '';
    var css = '';
    ['tablet', 'mobile'].forEach(function (tier) {
      var s = styles[tier];
      if (!s || typeof s !== 'object' || Array.isArray(s)) return;
      var decl = '';
      for (var k in TIER_PROPERTIES) {
        if (!Object.prototype.hasOwnProperty.call(s, k)) continue;
        var v = typeof s[k] === 'string' ? s[k].trim().toLowerCase() : '';
        if (TIER_VALUE.test(v)) decl += TIER_PROPERTIES[k] + ':' + v + ' !important;';
      }
      if (decl) css += '@media ' + TIER_MEDIA[tier] + '{[data-neo-tier="' + scope + '"]{' + decl + '}}';
    });
    return css;
  }

  // Element id reduced to [a-z0-9-], as the server renderer's safeElementId().
  function safeElementId(raw) {
    return String(raw == null ? '' : raw).toLowerCase().replace(/[^a-z0-9-]/g, '');
  }

  renderers.text = function (el) {
    var content = el.content || {};
    var text = withNativeTextMarks(content.text != null ? content.text : '');
    var classes = 'content-type-text content-type-neo-text';
    // content.linkTextSize 'body' (news import --native-text-settings=link-size): the words of a
    // link in a paragraph are drawn in the size of the paragraph around them (news.css), which is
    // what the bare span the legacy text wrapped them in drew.
    if (content.linkTextSize === 'body') classes += ' content-type-neo-link-size-body';
    if (content.paragraphSpacing !== undefined) text = withParagraphSpacing(text, content.paragraphSpacing);
    var tierAttr = '';
    var tierCss = '';
    var scope = safeElementId(el.id);
    if (scope && (el.styles && (el.styles.tablet || el.styles.mobile))) {
      tierCss = tierStyle(el, scope);
      if (tierCss) tierAttr = ' data-neo-tier="' + scope + '"';
    }
    return '<div class="' + classes + '"' + tierAttr + inlineStyleAttr(el) + '>' + text + '</div>' +
      (tierCss ? '<style>' + tierCss + '</style>' : '');
  };

  var IMAGE_SIZES = ['extra-small', 'small', 'medium', 'large', 'extra-large'];
  var IMAGE_ALIGNMENTS = ['left', 'center', 'right'];
  // Geometry the converter also expresses as styles.desktop for renderers that lack
  // the legacy classes. Here news.css carries it, so inlining it would double up.
  var LEGACY_IMAGE_GEOMETRY_KEYS = { maxWidth: 1, width: 1, marginLeft: 1, marginRight: 1, display: 1 };

  renderers.image = function (el) {
    var content = el.content || {};
    var src = content.src || '';
    if (!src) return '';
    var alt = escapeAttr(content.alt || '');
    var caption = content.caption || '';
    var size = IMAGE_SIZES.indexOf(content.size) !== -1 ? content.size : '';
    var alignment = IMAGE_ALIGNMENTS.indexOf(content.alignment) !== -1 ? content.alignment : '';
    var html;

    if (size || alignment) {
      // Legacy-converted article: the TEXT/LINK image container markup so the
      // .image-width--* / .image-text--* rules in news.css render it pixel-identical.
      var classes = ['content-type-neo-image', 'image-container'];
      if (size) classes.push('image-width--' + size);
      if (alignment) classes.push('image-text--' + alignment);
      html = '<div class="' + classes.join(' ') + '"' + (alignment ? ' style="text-align:' + alignment + '"' : '') + '>';
      html += '<div class="image-container-inner"' + inlineStyleAttrExcept(el, LEGACY_IMAGE_GEOMETRY_KEYS) + '>';
      html += wrapLink('<img src="' + escapeAttr(src) + '" alt="' + alt + '" class="lazyload" loading="lazy">', content);
      if (caption) {
        html += '<span class="caption">' + caption + '</span>';
      }
      html += '</div>';
      html += '</div>';
      return html;
    }

    // Inline styles go on the <img>: no legacy .image-container wrapper (forced max-width:75%).
    html = '<div class="content-type-neo-image">';
    html += wrapLink('<img src="' + escapeAttr(src) + '" alt="' + alt + '" loading="lazy"' + inlineStyleAttr(el) + '>', content);
    if (caption) {
      html += '<span class="caption">' + caption + '</span>';
    }
    html += '</div>';
    return html;
  };

  // Raw markup is sanitised server side; it goes to the DOM verbatim (including
  // <style>) inside the same <p> the legacy HTML branch used, so spacing stays
  // identical (news.js strips empty paragraphs afterwards, so the <p> is harmless).
  renderers.html = function (el) {
    var content = el.content || {};
    var raw = content.html != null ? String(content.html) : '';
    if (!raw) return '';
    return '<div class="content-type-html content-type-neo-html"><p>' + raw + '</p></div>';
  };

  // Legacy DOCUMENT idiom: <button class="document-button"><a>text</a></button>.
  // Colours are inline on the button; the hover colour needs a rule, scoped by a
  // per-element class so two buttons never overwrite each other.
  renderers.button = function (el) {
    var content = el.content || {};
    var href = normalizeUrl(content.url);
    var text = content.text != null ? content.text : '';
    if (!href || !text) return '';
    var st = pickStyles(el);
    var bg = cssValue(st.backgroundColor);
    var color = cssValue(st.color);
    var border = cssValue(st.borderColor);
    var hover = cssValue(st.hoverBackgroundColor);
    var scope = 'content-type-neo-button-' + (++scopedStyleSeq);
    var target = content.openInNewTab === true ? ' target="_blank" rel="noopener"' : '';

    var html = '<div class="content-type-document content-type-neo-button ' + scope + '">';
    if (bg) {
      if (hover) {
        // !important because the resting colour sits inline on the button and
        // would otherwise beat the stylesheet hover rule.
        html += '<style>.' + scope + ' .document-button:hover{background-color:' + hover + ' !important;}</style>';
      }
      var btnStyle = 'background-color:' + bg + ';' + (border ? 'border:1px solid ' + border + ';' : '') + 'padding:10px';
      html += '<button class="document-button" style="' + escapeStyleValue(btnStyle) + '">';
      html += '<a href="' + escapeAttr(href) + '"' + target + ' style="' + escapeStyleValue((color ? 'color:' + color + ';' : '') + 'text-decoration:none') + '">' + text + '</a>';
    } else {
      html += '<button class="">';
      html += '<a href="' + escapeAttr(href) + '"' + target + '>' + text + '</a>';
    }
    html += '</button>';
    html += '</div>';
    return html;
  };

  renderers.divider = function (el) {
    return '<div class="content-type-divider content-type-neo-divider"><hr' + inlineStyleAttr(el) + '></div>';
  };

  // ----- video helpers -----

  function youtubeId(url) {
    if (!url) return '';
    var m = String(url).match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    return m ? m[1] : '';
  }

  function vimeoId(url) {
    if (!url) return '';
    var m = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : '';
  }

  function parseAspectRatio(s, fallback) {
    if (!s) return fallback;
    var str = String(s).replace(':', '/');
    if (/^\d+(\.\d+)?(\/\d+(\.\d+)?)?$/.test(str)) return str;
    return fallback;
  }

  // Same embed URL parameters as the legacy VIDEO branch, so player behaviour
  // (JS API, no related videos) matches.
  function youtubeIframe(id, title) {
    return '<iframe title="' + escapeAttr(title) + '" src="' +
      escapeAttr(youtubeEmbedUrl(id)) +
      '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
  }

  // The player frame of a legacy news Custom-HTML block, kept by the news import on the native
  // youtube / video element as content.legacyNewsFrame, drawn as the block drew it. A player
  // element without the setting is drawn exactly as before (half the column, 360px high).
  //   aspect: the oembed paste of the rich-text editor (--legacy-oembed-players): the full-width
  //           box whose height is its bottom padding (legacyNewsFrameAspect, 56.25 for 16:9), the
  //           frame stretched over it, optionally inside the editor's bare <figure>
  //           (legacyNewsFrameFigure), in the paragraph of the html renderer. The same markup as a
  //           social-embed aspect frame, with the player's own frame.
  //   sized:  a frame at its own pixel size (legacyNewsFrameWidth x legacyNewsFrameHeight, the
  //           width and height attributes of the legacy frame, --legacy-player-frame-size): the
  //           box keeps its element settings, the frame is drawn at that size and across the
  //           column from 750px down, as the theme drew a frame of a Custom-HTML block, not at half
  //           of the box (which rounds a 640px frame to 638px in the 1276px column).
  // Anything else in the setting draws the player as before.
  var FRAME_ASPECT = /^\d{1,3}(?:\.\d{1,4})?$/;
  var FRAME_PIXELS = /^[1-9]\d{0,3}$/;

  // The box a legacy frame is drawn in: the paragraph of the html renderer for a frame that left a
  // Custom-HTML block (legacyNewsBlock html), the text box for an embed the editor pasted into a
  // TEXT block (legacyNewsBlock text, news import --prose-oembed-frames), whose font size the
  // figure's 1em margins follow (16px there, 14px in the html paragraph on a phone).
  function legacyFrameInBlock(markup, block) {
    return block === 'text'
      ? '<div class="content-type-text content-type-neo-text">' + markup + '</div>'
      : renderers.html({ content: { html: markup } });
  }

  // The box's bottom padding: the percentage, capped at a pixel height where the imported frame
  // carries the cap the legacy box had (padding-bottom: min(590px, 64.25%); legacyNewsFrameAspectCap).
  function legacyAspectPadding(aspect, cap) {
    return typeof cap === 'string' && FRAME_PIXELS.test(cap) ? 'min(' + cap + 'px,' + aspect + '%)' : aspect + '%';
  }

  function legacyAspectFrameMarkup(frame, aspect, figure, cap) {
    var markup = '<div class="video-wrapper" style="position:relative;display:block;width:100%;height:0;padding-bottom:' + legacyAspectPadding(aspect, cap) + '">' + frame + '</div>';
    return figure ? '<figure>' + markup + '</figure>' : markup;
  }

  // The player drawn from its legacy frame setting, or null when the element has none (or one
  // this renderer does not read): the caller then draws it as before.
  //   wrapperClass: the classes of the element's own box; frameAttrs(extra): the frame's opening
  //   attributes after the address, `extra` being the size/stretch attributes of the shape.
  function legacyPlayerFrame(el, wrapperClass, src, frameAttrs) {
    var content = el.content || {};
    var shape = content.legacyNewsFrame;
    if (shape === 'aspect') {
      var aspect = typeof content.legacyNewsFrameAspect === 'string' ? content.legacyNewsFrameAspect : '';
      if (content.legacyNewsMarkup !== true || (content.legacyNewsBlock !== 'html' && content.legacyNewsBlock !== 'text') ||
        !FRAME_ASPECT.test(aspect) || parseFloat(aspect) <= 0 || parseFloat(aspect) > 200) return null;
      var stretched = '<iframe src="' + escapeAttr(src) + '"' + frameAttrs(' width="100%" height="100%"') +
        ' style="position:absolute;top:0;left:0;width:100%;height:100%;border:0"></iframe>';
      return legacyFrameInBlock(legacyAspectFrameMarkup(stretched, aspect, content.legacyNewsFrameFigure === true, content.legacyNewsFrameAspectCap), content.legacyNewsBlock);
    }
    if (shape === 'sized') {
      var w = typeof content.legacyNewsFrameWidth === 'string' ? content.legacyNewsFrameWidth : '';
      var h = typeof content.legacyNewsFrameHeight === 'string' ? content.legacyNewsFrameHeight : '';
      if (!FRAME_PIXELS.test(w) || !FRAME_PIXELS.test(h)) return null;
      return '<div class="' + wrapperClass + ' content-type-neo-player-sized"' + inlineStyleAttr(el) + '>' +
        '<iframe src="' + escapeAttr(src) + '"' + frameAttrs(' width="' + w + '" height="' + h + '"') +
        ' style="--neo-frame-width:' + w + 'px;--neo-frame-height:' + h + 'px"></iframe>' +
        '</div>';
    }
    return null;
  }

  function youtubeEmbedUrl(id) {
    return 'https://www.youtube.com/embed/' + id + '?feature=oembed&enablejsapi=1&rel=0';
  }

  // The address a framed player plays: the element's own embed address when it is the player's
  // (the legacy frame's, as the import stored it), otherwise the theme's embed address.
  function framedYoutubeUrl(content, id) {
    var src = typeof content.src === 'string' ? content.src.trim() : '';
    var own = src.match(/^https:\/\/www\.youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{6,})(?:\?[^\s"'<>]*)?$/);
    return own && own[1] === id ? src : youtubeEmbedUrl(id);
  }

  // Both video renderers use the legacy .content-type-video wrapper without an
  // aspect-ratio frame: news.css sizes the iframe/video itself.
  renderers.youtube = function (el) {
    var content = el.content || {};
    var id = content.videoId || youtubeId(content.src || '');
    if (!id) return '';
    if (content.legacyNewsFrame !== undefined) {
      var title = escapeAttr(content.videoTitle || 'YouTube video');
      var framed = legacyPlayerFrame(el, 'content-type-video content-type-neo-youtube', framedYoutubeUrl(content, id), function (extra) {
        return ' title="' + title + '"' + extra + ' frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen';
      });
      if (framed !== null) return framed;
    }
    return '<div class="content-type-video content-type-neo-youtube"' + inlineStyleAttr(el) + '>' +
      youtubeIframe(id, content.videoTitle || 'YouTube video') +
      '</div>';
  };

  // legacyNewsVideoFluid (news import --legacy-fluid-video-tags): a <video> tag the rich-text
  // editor wrote as a centred block across the column between a minimum and a maximum width
  // (style="display:block;margin:0 auto;width:100%;min-width:240px;max-width:840px"). Drawn the
  // same way, with the two widths the element states, instead of at 80% of the column.
  var FLUID_VIDEO_WIDTH = /^[1-9]\d{1,3}$/;
  function legacyFluidVideo(content) {
    var fluid = content.legacyNewsVideoFluid;
    if (!fluid || typeof fluid !== 'object' || !FLUID_VIDEO_WIDTH.test(String(fluid.minWidth)) || !FLUID_VIDEO_WIDTH.test(String(fluid.maxWidth))) return null;
    return 'display:block;margin:0 auto;width:100%;min-width:' + fluid.minWidth + 'px;max-width:' + fluid.maxWidth + 'px';
  }

  renderers.video = function (el) {
    var content = el.content || {};
    var src = content.src || '';
    if (!src) return '';
    var type = content.type || 'custom';
    var inner;
    if (type === 'youtube') {
      var yid = youtubeId(src);
      if (!yid) return '';
      inner = youtubeIframe(yid, content.videoTitle || 'YouTube video');
    } else if (type === 'vimeo') {
      var vid = vimeoId(src);
      if (!vid) return '';
      if (content.legacyNewsFrame !== undefined) {
        var framed = legacyPlayerFrame(el, 'content-type-video content-type-neo-video', 'https://player.vimeo.com/video/' + vid, function (extra) {
          return extra + ' frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen';
        });
        if (framed !== null) return framed;
      }
      inner = '<iframe src="https://player.vimeo.com/video/' + escapeAttr(vid) +
        '" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
    } else {
      // legacyMinimized (news import --legacy-minimized-videos, written only as true): the author
      // set the uploaded video to "minimized", which the legacy article drew in the VideoMinimized
      // class (news.css caps its height at 600px). Without it the video is drawn as before.
      var minimized = content.legacyMinimized === true ? ' class="VideoMinimized"' : '';
      var fluid = legacyFluidVideo(content);
      if (fluid !== null) {
        return '<div class="content-type-video content-type-neo-video content-type-neo-video-fluid"' + inlineStyleAttr(el) + '>' +
          '<video controls src="' + escapeAttr(src) + '" style="' + fluid + '"></video></div>';
      }
      inner = '<video' + minimized + ' width="80%" controls><source src="' + escapeAttr(src) + '"></video>';
    }
    return '<div class="content-type-video content-type-neo-video"' + inlineStyleAttr(el) + '>' + inner + '</div>';
  };

  renderers.gallery = function (el) {
    var content = el.content || {};
    var rawImages = Array.isArray(content.images) ? content.images : [];
    var images = [];
    for (var i = 0; i < rawImages.length; i++) {
      if (rawImages[i] && rawImages[i].src) images.push(rawImages[i]);
    }
    if (images.length === 0) return '';

    var cols = parseInt(content.columns, 10);
    if (!isFinite(cols) || cols < 1) cols = 3;
    if (cols > 6) cols = 6;

    var variant = content.layoutVariant
      ? ' content-type-neo-gallery--' + escapeAttr(String(content.layoutVariant))
      : '';

    var apiStyles = USE_API_INLINE_STYLES ? stylesToInline(pickStyles(el)) : '';
    var gridCols = 'grid-template-columns:repeat(' + cols + ',1fr)';
    var combined = apiStyles ? apiStyles + ';' + gridCols : gridCols;

    var html = '<div class="content-type-neo-gallery' + variant + '" style="' + escapeStyleValue(combined) + '">';
    for (var k = 0; k < images.length; k++) {
      var img = images[k];
      html += '<img src="' + escapeAttr(img.src) + '" alt="' + escapeAttr(img.alt || '') + '" loading="lazy">';
    }
    html += '</div>';
    return html;
  };

  function renderMediaGalleryItem(file, showCaptions) {
    var src = file.optimizedImageUrl || file.url;
    var t = pickTranslation(file.translations);
    var alt = escapeAttr(t.alternativeText || t.name || '');
    var caption = t.caption || '';
    var html = '<figure class="neo-media-gallery__item">';
    html += '<img src="' + escapeAttr(src) + '" alt="' + alt + '" loading="lazy">';
    if (showCaptions && caption) {
      html += '<figcaption class="neo-media-gallery__caption">' + caption + '</figcaption>';
    }
    html += '</figure>';
    return html;
  }

  // Legacy ALBUM slider markup (slick .slider-for + .slider-nav), so news.css and
  // the slick init in news.js treat a converted album exactly like a legacy one.
  // No inline API styles: the legacy wrapper geometry lives in news.css.
  function renderLegacyAlbum(files, subsectionTitle) {
    var albumId = 'neo-album-' + (++scopedStyleSeq);
    var html = '<div class="news-media-wrapper content-type-content-album content-type-neo-media-gallery" data-album="' + albumId + '">';
    if (subsectionTitle) html += '<h3 class="neo-media-gallery__title">' + subsectionTitle + '</h3>';
    html += '<div class="news-slider slider-for">';
    for (var i = 0; i < files.length; i++) {
      var file = files[i];
      var src = escapeAttr(file.optimizedImageUrl || file.url);
      var t = pickTranslation(file.translations);
      var caption = t.caption || t.alternativeText || '';
      var order = typeof file.order === 'number' ? file.order : i;
      html += '<div class="news-images" data-order="' + order + '">';
      html += '<img src="' + src + '" width="1300" alt="">';
      if (caption) html += '<span class="caption">' + caption + '</span>';
      html += '</div>';
    }
    html += '</div>';
    html += '<div class="news-thumbnail slider-nav">';
    for (var j = 0; j < files.length; j++) {
      var thumb = files[j];
      var thumbSrc = escapeAttr(thumb.optimizedImageUrl || thumb.url);
      var thumbOrder = typeof thumb.order === 'number' ? thumb.order : j;
      html += '<div class="news-thumbnail-images" data-thumbnail-order="' + thumbOrder + '">';
      html += '<img src="' + thumbSrc + '" alt="" width="150">';
      html += '</div>';
    }
    html += '</div>';
    html += '</div>';
    return html;
  }

  // A legacy CAROUSEL block converted to the album slider (news import --legacy-carousel-slider):
  // a carousel gallery whose pictures are inline in the element and which names no media album,
  // exactly as that import writes it (shouldOmitUnsetFileFields, no mediaGalleryAlbumId). An album
  // always names its media album, so it is never read as a carousel.
  function isLegacyCarousel(content) {
    return content.shouldOmitUnsetFileFields === true &&
      !Object.prototype.hasOwnProperty.call(content, 'mediaGalleryAlbumId') &&
      !content.subsectionTitle;
  }

  // The legacy CAROUSEL markup of news.js (.content-type-carousel around the same slider-for /
  // slider-nav pair as an album), so news.css draws it as the carousel was drawn: 80% of the
  // column with its own margins, where an album is drawn at the full column width. Each picture's
  // caption is its title, as news.js printed it.
  function renderLegacyCarousel(files) {
    var html = '<div class="content-type-carousel content-type-neo-carousel">';
    html += '<div class="news-media-wrapper content-type-neo-media-gallery">';
    html += '<div class="news-slider slider-for">';
    for (var i = 0; i < files.length; i++) {
      var t = pickTranslation(files[i].translations);
      html += '<div class="news-images">';
      html += '<img src="' + escapeAttr(files[i].optimizedImageUrl || files[i].url) + '" width="1300" alt="">';
      if (t.caption) html += '<span class="caption">' + t.caption + '</span>';
      html += '</div>';
    }
    html += '</div>';
    html += '<div class="news-thumbnail slider-nav">';
    for (var j = 0; j < files.length; j++) {
      html += '<div class="news-thumbnail-images">';
      html += '<img src="' + escapeAttr(files[j].optimizedImageUrl || files[j].url) + '" alt="" width="150">';
      html += '</div>';
    }
    html += '</div>';
    html += '</div>';
    html += '</div>';
    return html;
  }

  // An empty gallery is a converter or data defect worth surfacing, not a silent skip.
  function warnNoFiles(el) {
    if (window.console && console.warn) {
      console.warn('NewsNeo: media-gallery without files', el);
    }
    return '';
  }

  renderers['media-gallery'] = function (el) {
    var content = el.content || {};
    var rawFiles = Array.isArray(content.files) ? content.files : [];
    if (rawFiles.length === 0) return warnNoFiles(el);

    var files = rawFiles.slice().sort(function (a, b) {
      var ao = (a && typeof a.order === 'number') ? a.order : 0;
      var bo = (b && typeof b.order === 'number') ? b.order : 0;
      return ao - bo;
    }).filter(function (f) {
      return f && (f.url || f.optimizedImageUrl);
    });
    if (files.length === 0) return warnNoFiles(el);

    var cols = parseInt(content.columns, 10);
    if (!isFinite(cols) || cols < 1) cols = 3;
    if (cols > 6) cols = 6;

    var layout = (content.layout || 'grid').toLowerCase();
    if (layout !== 'grid' && layout !== 'masonry' && layout !== 'carousel') layout = 'grid';

    var showCaptions = content.showCaptions !== false;
    var subsectionTitle = content.subsectionTitle || '';
    var variantClass = ' content-type-neo-media-gallery--' + layout;

    var inner = '';
    if (layout === 'masonry') {
      inner += '<div class="neo-media-gallery__masonry" style="column-count:' + cols + ';">';
      for (var i = 0; i < files.length; i++) inner += renderMediaGalleryItem(files[i], showCaptions);
      inner += '</div>';
    } else if (layout === 'carousel') {
      if (isLegacyCarousel(content)) return renderLegacyCarousel(files);
      return renderLegacyAlbum(files, subsectionTitle);
    } else {
      inner += '<div class="neo-media-gallery__grid" style="grid-template-columns:repeat(' + cols + ',1fr);">';
      for (var k = 0; k < files.length; k++) inner += renderMediaGalleryItem(files[k], showCaptions);
      inner += '</div>';
    }

    var html = '<div class="content-type-neo-media-gallery' + variantClass + '"' + inlineStyleAttr(el) + '>';
    if (subsectionTitle) html += '<h3 class="neo-media-gallery__title">' + subsectionTitle + '</h3>';
    html += inner;
    html += '</div>';
    return html;
  };

  renderers.slideshow = function (el) {
    var content = el.content || {};
    var rawSlides = Array.isArray(content.slides) ? content.slides : [];
    var slides = [];
    for (var i = 0; i < rawSlides.length; i++) {
      if (rawSlides[i] && rawSlides[i].src) slides.push(rawSlides[i]);
    }
    if (slides.length === 0) return '';

    var autoplay = content.autoplay === true;
    var interval = parseInt(content.autoplayInterval, 10);
    if (!isFinite(interval) || interval < 1000) interval = 5000;
    var showDots = content.showDots !== false;
    var showArrows = content.showArrows !== false;
    var aspect = parseAspectRatio(content.aspectRatio, '16/9');

    var html = '<div class="content-type-text content-type-neo-slideshow"' +
      ' data-neo-slideshow="1"' +
      ' data-autoplay="' + (autoplay ? '1' : '0') + '"' +
      ' data-interval="' + interval + '"' +
      inlineStyleAttr(el) + '>';
    html += '<div class="neo-slideshow__viewport" style="aspect-ratio:' + aspect + ';">';
    html += '<div class="neo-slideshow__track">';
    for (var s = 0; s < slides.length; s++) {
      var slide = slides[s];
      html += '<div class="neo-slideshow__slide">';
      html += '<img src="' + escapeAttr(slide.src) + '" alt="' + escapeAttr(slide.alt || '') + '" loading="lazy">';
      if (slide.headline || (slide.buttonText && slide.buttonUrl)) {
        html += '<div class="neo-slideshow__caption">';
        if (slide.headline) html += '<h3 class="neo-slideshow__headline" style="color:#fff !important;font-size:28px !important;font-weight:600 !important;line-height:1.2 !important;margin:0 !important;">' + slide.headline + '</h3>';
        var btnUrl = normalizeUrl(slide.buttonUrl);
        if (slide.buttonText && btnUrl) {
          html += '<a class="neo-slideshow__btn" href="' + escapeAttr(btnUrl) + '">' + slide.buttonText + '</a>';
        }
        html += '</div>';
      }
      html += '</div>';
    }
    html += '</div>'; // track
    if (showArrows && slides.length > 1) {
      html += '<button type="button" class="neo-slideshow__nav neo-slideshow__nav--prev" aria-label="Previous slide">&#10094;</button>';
      html += '<button type="button" class="neo-slideshow__nav neo-slideshow__nav--next" aria-label="Next slide">&#10095;</button>';
    }
    html += '</div>'; // viewport
    if (showDots && slides.length > 1) {
      html += '<div class="neo-slideshow__dots">';
      for (var d = 0; d < slides.length; d++) {
        html += '<button type="button" class="neo-slideshow__dot" data-idx="' + d + '" aria-label="Go to slide ' + (d + 1) + '"></button>';
      }
      html += '</div>';
    }
    html += '</div>';
    return html;
  };

  // ----- legacy news tables and player frames -----

  // A table cell's text reduced to the inline tags the neo table element draws (a, strong, em,
  // br), everything else unwrapped to its content, as the server renderer's sanitizeTableCellHtml().
  var TABLE_CELL_TAGS = { A: 1, STRONG: 1, EM: 1, BR: 1 };
  function tableCellHtml(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = String(html == null ? '' : html);
    function walk(node) {
      var out = '';
      for (var c = node.firstChild; c; c = c.nextSibling) {
        if (c.nodeType === 3) { out += escapeAttr(c.nodeValue).replace(/&quot;/g, '"').replace(/\u00a0/g, '&nbsp;'); continue; }
        if (c.nodeType !== 1) continue;
        var tag = c.tagName;
        if (!TABLE_CELL_TAGS[tag]) { out += walk(c); continue; }
        if (tag === 'BR') { out += '<br>'; continue; }
        if (tag === 'A') {
          var href = normalizeUrl(c.getAttribute('href'));
          var attrs = href ? ' href="' + escapeAttr(href) + '"' : '';
          var target = c.getAttribute('target');
          if (target === '_blank') attrs += ' target="_blank" rel="noopener"';
          out += '<a' + attrs + '>' + walk(c) + '</a>';
          continue;
        }
        out += '<' + tag.toLowerCase() + '>' + walk(c) + '</' + tag.toLowerCase() + '>';
      }
      return out;
    }
    return walk(tpl.content);
  }

  // A length the table may carry into a style attribute; a bare number is pixels.
  function tableLength(v) {
    var w = (typeof v === 'string' || typeof v === 'number') ? String(v).trim().toLowerCase() : '';
    if (/^\d+(\.\d+)?$/.test(w)) w += 'px';
    return /^\d+(\.\d+)?(px|rem|em|vh|vw|%)$/.test(w) ? w : '';
  }

  function tableSpan(cell) {
    var out = '';
    [['colSpan', 'colspan'], ['rowSpan', 'rowspan']].forEach(function (p) {
      var v = cell[p[0]];
      if (typeof v === 'string' && /^\d+$/.test(v)) v = parseInt(v, 10);
      if (typeof v === 'number' && isFinite(v) && Math.floor(v) === v && v > 1) out += ' ' + p[1] + '="' + Math.min(v, 100) + '"';
    });
    return out;
  }

  function tableAlign(v) {
    v = typeof v === 'string' ? v.trim().toLowerCase() : '';
    return v === 'left' || v === 'center' || v === 'right' ? v : '';
  }

  // ----- legacy news tables drawn with their own look (content.legacyNewsTable) -----
  //
  // A table of a legacy news Custom-HTML or text block that the news import kept with the look its
  // author gave it (news import --legacy-table-look, with --native-tables): the border, spacing and
  // padding the table stated, its width, alignment and margins, and per cell the width, height,
  // alignment, padding, lines and fill. The settings are read from a closed vocabulary (below) and
  // drawn as the browser drew the legacy markup: the same table and cell attributes, and the stated
  // declarations, each value checked against the pattern of its property. A table without the
  // setting is drawn by the renderer below, exactly as before; an unreadable value is left out.
  var LOOK_LENGTH = '[+-]?(?:\\d+(?:\\.\\d+)?|\\.\\d+)(?:px|pt|cm|mm|in|pc|em|rem|%)?';
  var LOOK_COLOR = '(?:#[0-9a-f]{3}|#[0-9a-f]{6}|[a-z]{3,20}|rgb\\(\\s*\\d{1,3}\\s*,\\s*\\d{1,3}\\s*,\\s*\\d{1,3}\\s*\\))';
  var LOOK_BORDER_STYLE = '(?:none|hidden|dotted|dashed|solid|double|groove|ridge|inset|outset)';
  var LOOK_BORDER_WIDTH = '(?:thin|medium|thick|' + LOOK_LENGTH + ')';
  function lookPattern(token, max) { return new RegExp('^' + token + '(?:\\s+' + token + '){0,' + (max - 1) + '}$', 'i'); }
  var LOOK_VALUE = {
    lengths: lookPattern('(?:auto|' + LOOK_LENGTH + ')', 4),
    length: new RegExp('^(?:auto|' + LOOK_LENGTH + ')$', 'i'),
    border: lookPattern('(?:' + LOOK_BORDER_WIDTH + '|' + LOOK_BORDER_STYLE + '|' + LOOK_COLOR + ')', 3),
    borderWidth: lookPattern(LOOK_BORDER_WIDTH, 4),
    borderStyle: lookPattern(LOOK_BORDER_STYLE, 4),
    color: lookPattern(LOOK_COLOR, 4),
    background: lookPattern('(?:none|repeat|no-repeat|scroll|fixed|' + LOOK_LENGTH + '|' + LOOK_COLOR + ')', 6),
    verticalAlign: /^(?:top|middle|bottom|baseline)$/i,
    textAlign: /^(?:left|center|right|justify)$/i,
    whiteSpace: /^(?:normal|nowrap|pre|pre-wrap|pre-line)$/i,
    borderCollapse: /^(?:collapse|separate)$/i
  };
  // The declarations a legacy table or cell may carry, each with the pattern its value must match.
  var LOOK_DECLARATIONS = {
    width: 'length', height: 'length',
    margin: 'lengths', marginTop: 'length', marginRight: 'length', marginBottom: 'length', marginLeft: 'length',
    padding: 'lengths', paddingTop: 'length', paddingRight: 'length', paddingBottom: 'length', paddingLeft: 'length',
    border: 'border', borderTop: 'border', borderRight: 'border', borderBottom: 'border', borderLeft: 'border',
    borderWidth: 'borderWidth', borderStyle: 'borderStyle', borderColor: 'color',
    background: 'background', backgroundColor: 'color',
    verticalAlign: 'verticalAlign', textAlign: 'textAlign', whiteSpace: 'whiteSpace', borderCollapse: 'borderCollapse'
  };
  var LOOK_TABLE_ATTRIBUTES = { border: /^\d{1,2}$/, cellspacing: /^\d{1,2}$/, cellpadding: /^\d{1,2}$/, width: /^\d{1,4}%?$/, height: /^\d{1,4}%?$/, align: /^(?:left|center|right)$/ };
  var LOOK_CELL_ATTRIBUTES = { width: /^\d{1,4}%?$/, height: /^\d{1,4}%?$/, valign: /^(?:top|middle|bottom|baseline)$/, align: /^(?:left|center|right|justify)$/, nowrap: /^nowrap$/ };

  // The attributes (in their stored order) and the style declarations of a table or cell look.
  function lookAttributes(look, allowed) {
    var out = '';
    var attrs = look && typeof look.attributes === 'object' && !Array.isArray(look.attributes) ? look.attributes : {};
    for (var k in attrs) {
      if (!Object.prototype.hasOwnProperty.call(attrs, k) || !allowed[k]) continue;
      var v = typeof attrs[k] === 'string' ? attrs[k].trim().toLowerCase() : '';
      if (allowed[k].test(v)) out += ' ' + k + '="' + v + '"';
    }
    var style = look && typeof look.style === 'object' && !Array.isArray(look.style) ? look.style : {};
    var decl = [];
    for (var p in style) {
      if (!Object.prototype.hasOwnProperty.call(style, p) || !LOOK_DECLARATIONS[p]) continue;
      var val = typeof style[p] === 'string' ? style[p].trim().replace(/\s+/g, ' ') : '';
      if (val && LOOK_VALUE[LOOK_DECLARATIONS[p]].test(val)) decl.push(camelToKebab(p) + ':' + val);
    }
    return out + (decl.length ? ' style="' + decl.join(';') + '"' : '');
  }

  // A legacy cell's words, reduced by the import to the marks the text element carries: paragraphs
  // (data-margin: the margins the legacy paragraph stated; data-align: its alignment), line breaks,
  // bold, italic, underline, links, headings and the text-size mark of a legacy font size.
  var LOOK_CELL_TAGS = { P: 1, BR: 1, STRONG: 1, EM: 1, U: 1, A: 1, SMALL: 1, SUB: 1, SUP: 1, H1: 1, H2: 1, H3: 1, H4: 1, H5: 1, H6: 1 };
  var LOOK_SIZES = { small: 1, 'x-small': 1 };
  function legacyCellHtml(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = String(html == null ? '' : html);
    function walk(node) {
      var out = '';
      for (var c = node.firstChild; c; c = c.nextSibling) {
        if (c.nodeType === 3) { out += escapeAttr(c.nodeValue).replace(/&quot;/g, '"').replace(/\u00a0/g, '&nbsp;'); continue; }
        if (c.nodeType !== 1) continue;
        var tag = c.tagName;
        if (!LOOK_CELL_TAGS[tag]) { out += walk(c); continue; }
        if (tag === 'BR') { out += '<br>'; continue; }
        var attrs = '';
        var keep = false;
        if (tag === 'A') {
          var href = normalizeUrl(c.getAttribute('href'));
          if (href) attrs += ' href="' + escapeAttr(href) + '"';
          if (c.getAttribute('target') === '_blank') attrs += ' target="_blank" rel="noopener"';
        } else if (tag === 'SMALL') {
          // data-run: the text run marks of a text (withNativeTextMarks), written back as the tag
          // they stand for: a legacy font tag or the body span, drawn by the theme's rules for it.
          var run = c.getAttribute('data-run');
          if (run && Object.prototype.hasOwnProperty.call(TEXT_RUN_MARKS, run)) {
            out += TEXT_RUN_MARKS[run][0] + walk(c) + TEXT_RUN_MARKS[run][1];
            continue;
          }
          var size = c.getAttribute('data-size');
          if (LOOK_SIZES[size]) attrs += ' data-size="' + size + '"';
        } else if (tag === 'P') {
          // data-keep: a paragraph holding only blank space that the legacy page kept (its blank
          // space sat inside a tag), which news.js would otherwise strip as an empty paragraph; an
          // empty comment, which draws nothing, keeps it out of that pass.
          if (c.getAttribute('data-keep') === '1') keep = true;
          var decl = [];
          var margin = (c.getAttribute('data-margin') || '').trim();
          if (margin && LOOK_VALUE.lengths.test(margin)) decl.push('margin:' + margin);
          var align = (c.getAttribute('data-align') || '').trim().toLowerCase();
          if (LOOK_VALUE.textAlign.test(align)) decl.push('text-align:' + align);
          if (decl.length) attrs += ' style="' + decl.join(';') + '"';
        }
        var name = tag.toLowerCase();
        out += '<' + name + attrs + '>' + (keep ? '<!---->' : '') + walk(c) + '</' + name + '>';
      }
      return out;
    }
    return walk(tpl.content);
  }

  function rowCells(row) {
    var cells = [];
    if (Array.isArray(row)) cells = row;
    else if (row && typeof row === 'object') for (var key in row) if (Object.prototype.hasOwnProperty.call(row, key)) cells.push(row[key]);
    return cells.filter(function (c) { return c && typeof c === 'object' && !Array.isArray(c); });
  }

  function renderLegacyLookTable(content, block) {
    // The look carries its own rows; content.rows stays the plain table for a renderer without the look.
    var rows = Array.isArray(content.legacyNewsTable.rows) ? content.legacyNewsTable.rows : [];
    var body = '';
    for (var r = 0; r < rows.length; r++) {
      var cells = rowCells(rows[r]);
      var tr = '';
      for (var c = 0; c < cells.length; c++) {
        tr += '<td' + tableSpan(cells[c]) + lookAttributes(cells[c].legacyNewsCell, LOOK_CELL_ATTRIBUTES) + '>' + legacyCellHtml(cells[c].text) + '</td>';
      }
      body += '<tr>' + tr + '</tr>';
    }
    if (!body) return '';
    // classed: the legacy table wore a class attribute, so the theme's rules for a table without one
    // (base.css table:not([class]): fixed layout, collapsed 14px cells padded 1em) never reached it.
    var classed = content.legacyNewsTable.classed === true ? ' class="content-type-neo-table-look"' : '';
    return '<div class="content-type-' + block + ' content-type-neo-table">' +
      '<table' + classed + lookAttributes(content.legacyNewsTable, LOOK_TABLE_ATTRIBUTES) + '><tbody>' + body + '</tbody></table></div>';
  }

  // A table an author wrote inside a legacy news Custom-HTML or text block (news import
  // --native-tables), drawn as that block drew it and as the server renderer's
  // renderLegacyNewsProseTable() draws it: a plain table in the block's own box, every row in one
  // tbody, the column widths on the cells, the table's own width on the table. Only an element the
  // import marked (legacyNewsMarkup, legacyNewsBlock html or text) is drawn; any other table is
  // left to renderUnknown, as before.
  renderers.table = function (el) {
    var content = el.content || {};
    var block = content.legacyNewsBlock;
    if (content.legacyNewsMarkup !== true || (block !== 'html' && block !== 'text')) return renderUnknown(el);
    if (content.legacyNewsTable !== undefined && typeof content.legacyNewsTable === 'object' && content.legacyNewsTable !== null && !Array.isArray(content.legacyNewsTable)) {
      return renderLegacyLookTable(content, block);
    }
    var rows = Array.isArray(content.rows) ? content.rows : [];
    var columns = Array.isArray(content.columns) ? content.columns : [];
    var hasHeaderRow = content.headerRow === true;
    var head = '';
    var body = '';
    for (var r = 0; r < rows.length; r++) {
      var row = rows[r];
      var cells = [];
      if (Array.isArray(row)) cells = row;
      else if (row && typeof row === 'object') for (var key in row) if (Object.prototype.hasOwnProperty.call(row, key)) cells.push(row[key]);
      cells = cells.filter(function (c) { return c && typeof c === 'object' && !Array.isArray(c); });
      if (!cells.length) continue;
      var isHeader = hasHeaderRow && r === 0;
      var hasSpans = cells.some(function (c) { return tableSpan(c) !== ''; });
      var tr = '';
      for (var c = 0; c < cells.length; c++) {
        var col = columns[c] && typeof columns[c] === 'object' ? columns[c] : {};
        var align = tableAlign(cells[c].align) || tableAlign(col.align);
        var decl = [];
        var cw = hasSpans ? '' : tableLength(col.width);
        if (cw) decl.push('width:' + cw);
        if (align) decl.push('text-align:' + align);
        var tag = isHeader ? 'th' : 'td';
        tr += '<' + tag + (decl.length ? ' style="' + decl.join(';') + '"' : '') + tableSpan(cells[c]) + '>' + tableCellHtml(cells[c].text) + '</' + tag + '>';
      }
      if (isHeader) head += '<tr>' + tr + '</tr>';
      else body += '<tr>' + tr + '</tr>';
    }
    if (!head && !body) return '';
    var width = tableLength(content.legacyNewsWidth);
    var caption = typeof content.caption === 'string' ? content.caption.trim() : '';
    var table = '<table style="width:' + (width || '100%') + ';border-collapse:collapse">';
    if (caption) table += '<caption style="caption-side:top;text-align:left;padding:0 0 8px;font-weight:600">' + escapeAttr(caption) + '</caption>';
    if (head) table += '<thead>' + head + '</thead>';
    if (body) table += '<tbody>' + body + '</tbody>';
    table += '</table>';
    return '<div class="content-type-' + block + ' content-type-neo-table">' + table + '</div>';
  };

  // A third-party player frame that left a legacy news Custom-HTML block (news import
  // --legacy-video-frames), rebuilt from its settings as the server renderer's
  // legacyNewsPlayerFrameMarkup() does and placed in the paragraph of the html renderer:
  //   aspect: the full-width box whose height is its bottom padding, the frame stretched over it,
  //           so the player is the column's width at the box's aspect (16:9 for 56.25);
  //   inline: the frame on its own at 100%.
  //   fixed:  the frame on its own at the full column width and a fixed height, in pixels or in
  //           percent of the viewport height (legacyNewsFrameHeight, for example 480px or 50vh),
  //           as a podcast or widget frame was pasted. legacyNewsFrameSandbox (written only as
  //           true) keeps the restriction the legacy frame carried: sandbox="allow-same-origin
  //           allow-scripts" and referrerpolicy="no-referrer".
  // (legacyNewsFrameFigure, below, is a proposed key: the converter does not write it yet.)
  // Any other social embed is left to renderUnknown, as before.
  var FRAME_URL = /^https:\/\/[a-z0-9.-]+\.[a-z]{2,}(?:[\/?][^\s"'<>]*)?$/i;
  var FRAME_HEIGHT = /^(?:[1-9]\d{0,3}px|(?:[1-9]\d?|100)vh)$/;
  // hidden: an ad server's banner frame that the legacy article carried hidden and sizeless
  // (display:none, 0x0; news import --legacy-hidden-ad-frames). Drawn the same way: hidden, with no
  // box, so the article keeps the frame and its geometry. Its address may be http, as the legacy one was.
  var HIDDEN_FRAME_URL = /^https?:\/\/[a-z0-9.-]+\.[a-z]{2,}\/[^\s"'<>]*$/i;
  function hiddenLegacyFrame(content) {
    var src = typeof content.embedUrl === 'string' ? content.embedUrl.trim() : '';
    if (!HIDDEN_FRAME_URL.test(src)) return '';
    return '<div class="content-type-neo-hidden-frame" style="display:none !important"><iframe src="' + escapeAttr(src) +
      '" frameborder="0" scrolling="no" style="width:0;height:0;border:0"></iframe></div>';
  }

  renderers['social-embed'] = function (el) {
    var content = el.content || {};
    var shape = content.legacyNewsFrame;
    if (shape === 'hidden' && content.legacyNewsMarkup === true && content.legacyNewsBlock === 'html') return hiddenLegacyFrame(content);
    if (content.legacyNewsMarkup !== true || (shape !== 'aspect' && shape !== 'inline' && shape !== 'fixed')
      || (content.legacyNewsBlock !== 'html' && !(content.legacyNewsBlock === 'text' && shape === 'aspect'))) return renderUnknown(el);
    var src = typeof content.embedUrl === 'string' ? content.embedUrl.trim() : '';
    if (!FRAME_URL.test(src) || normalizeUrl(src) !== src) return '';
    var markup;
    if (shape === 'inline') {
      markup = '<iframe src="' + escapeAttr(src) + '" width="100%" height="100%" scrolling="no" allowfullscreen style="border:0;margin-bottom:0"></iframe>';
    } else if (shape === 'fixed') {
      var height = typeof content.legacyNewsFrameHeight === 'string' ? content.legacyNewsFrameHeight : '';
      if (!FRAME_HEIGHT.test(height)) return '';
      var restricted = content.legacyNewsFrameSandbox === true ? ' sandbox="allow-same-origin allow-scripts" referrerpolicy="no-referrer"' : '';
      markup = '<iframe src="' + escapeAttr(src) + '" scrolling="no"' + restricted + ' style="width:100%;height:' + height + ';border:0"></iframe>';
    } else {
      var aspect = typeof content.legacyNewsFrameAspect === 'string' ? content.legacyNewsFrameAspect : '';
      if (!/^\d{1,3}(?:\.\d{1,4})?$/.test(aspect) || parseFloat(aspect) <= 0 || parseFloat(aspect) > 200) return '';
      // legacyNewsFrameFigure: the editor pasted the box inside a <figure> (an oembed paste), whose
      // default margins (1em 40px) drew the player 80px narrower than the column. Written only as
      // true; without it the box is drawn at the full column width.
      markup = legacyAspectFrameMarkup('<iframe src="' + escapeAttr(src) + '" width="100%" height="100%" scrolling="no" allowfullscreen style="position:absolute;top:0;left:0;width:100%;height:100%;border:0"></iframe>',
        aspect, content.legacyNewsFrameFigure === true, content.legacyNewsFrameAspectCap);
    }
    return legacyFrameInBlock(markup, content.legacyNewsBlock);
  };

  function renderUnknown(el) {
    if (window.console && console.warn) {
      console.warn('NewsNeo: no renderer for type', el && el.type, el);
    }
    return '';
  }

  // ============================================================
  // Dispatch (single element, NOT a column container)
  // ============================================================

  function renderElement(el) {
    if (!el || typeof el !== 'object') return '';
    if (el.type === 'columns') {
      // Should never reach here - handled in renderSection. Safe guard.
      return '';
    }
    var fn = renderers[el.type];
    if (typeof fn !== 'function') return renderUnknown(el);
    try {
      return fn(el);
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('NewsNeo: renderer for "' + el.type + '" threw', e);
      }
      return '';
    }
  }

  // ============================================================
  // Row / column wrappers
  // Mirrors the legacy structure produced inside news.js so news.css
  // styles the result identically.
  // ============================================================

  function wrapContent(innerHtml, contentIndex) {
    if (!innerHtml) return '';
    return '<div class="content content__' + contentIndex + '">' + innerHtml + '</div>';
  }

  function wrapColumn(contentHtmlList, columnIndex) {
    var inner = contentHtmlList.join('');
    return '<div class="content-column column__' + columnIndex + '">' + inner + '</div>';
  }

  function wrapRow(columnsHtml, rowIndex, totalColumns, isFullWidth) {
    var classes = ['content-row', 'row__' + rowIndex, 'total-columns-' + totalColumns];
    if (isFullWidth) classes.push('fullwidth');
    return '<div class="' + classes.join(' ') + '">' + columnsHtml + '</div>';
  }

  // Render a `columns` element as one multi-column row.
  function renderColumnsRow(columnsEl, rowIndex, isFullWidth) {
    var content = columnsEl.content || {};
    var count = parseInt(content.columnCount, 10);
    if (isNaN(count) || count < 1) count = 2;
    if (count > 4) count = 4; // legacy CSS supports up to total-columns-4

    var children = Array.isArray(columnsEl.children) ? columnsEl.children : [];

    var sorted = children.slice().sort(function (a, b) {
      var ai = (a && a.content && typeof a.content.columnIndex === 'number') ? a.content.columnIndex : 0;
      var bi = (b && b.content && typeof b.content.columnIndex === 'number') ? b.content.columnIndex : 0;
      return ai - bi;
    });

    var buckets = [];
    for (var c = 0; c < count; c++) buckets.push([]);
    var fallbackIdx = 0;
    for (var j = 0; j < sorted.length; j++) {
      var child = sorted[j];
      var ci = (child && child.content && typeof child.content.columnIndex === 'number')
        ? child.content.columnIndex
        : fallbackIdx++;
      if (ci < 0) ci = 0;
      if (ci >= count) ci = count - 1;
      buckets[ci].push(child);
    }

    var columnsHtml = '';
    for (var col = 0; col < count; col++) {
      var contentsHtml = [];
      for (var k = 0; k < buckets[col].length; k++) {
        var rendered = renderElement(buckets[col][k]);
        if (rendered) contentsHtml.push(wrapContent(rendered, k + 1));
      }
      columnsHtml += wrapColumn(contentsHtml, col + 1);
    }

    return wrapRow(columnsHtml, rowIndex, count, isFullWidth);
  }

  // legacyNewsLine "join" / "join-space" (news import --legacy-picture-lines): a picture, or the
  // blank space between two pictures, that the legacy article drew on the line before it: the
  // last line of the paragraph of the text before it, or the line of the picture before it.
  // It is drawn there again: the picture as its bare <img> (in its link, if it has one), the blank
  // space as its no-break spaces, a blank placeholder frame as that frame; "join-space" first puts
  // back the space that stood before the picture. Only after a text element whose markup ends with
  // a paragraph, or after a picture drawn without a size or alignment; anything else is drawn as
  // before, on a line of its own.
  var LINE_BLANK = /^<p>((?:&nbsp;|\u00a0| )+)<\/p>$/;
  var LINE_TEXT_TAIL = /<\/p><\/div><\/div>$/;
  var LINE_PICTURE = /^<div class="content content__\d+"><div class="content-type-neo-image">(?:<a [^<>]*>)?<img [^<>]*>(?:<\/a>)?/;
  var LINE_PICTURE_TAIL = /<\/div><\/div>$/;
  function joinLegacyLine(previous, el) {
    var content = el && el.content;
    var mark = content && content.legacyNewsLine;
    if (mark !== 'join' && mark !== 'join-space') return null;
    var inline = '';
    if (el.type === 'image') {
      if (content.size || content.alignment || content.caption) return null;
      var m = /^<div class="content-type-neo-image">((?:<a [^<>]*>)?<img [^<>]*>(?:<\/a>)?)<\/div>$/.exec(renderers.image(el) || '');
      if (!m) return null;
      inline = m[1];
    } else if (el.type === 'social-embed') {
      // The blank placeholder frame of a slide-in script (news import --legacy-blank-frame-lines):
      // as wide as the column and without height, so it drew a line of its own under the words.
      if (content.legacyNewsFrame !== 'blank' || content.embedUrl !== 'about:blank') return null;
      inline = '<iframe src="about:blank" width="100%" height="0" frameborder="0" scrolling="no" style="margin:10px 0 0"></iframe>';
      if (mark !== 'join') return null;
    } else if (el.type === 'text') {
      var blank = LINE_BLANK.exec(String(content.text || ''));
      if (!blank) return null;
      inline = blank[1].replace(/\u00a0/g, '&nbsp;');
    } else {
      return null;
    }
    if (mark === 'join-space') inline = ' ' + inline;
    if (previous.indexOf('<div class="content content__') === 0 && previous.indexOf('content-type-neo-text') !== -1 && LINE_TEXT_TAIL.test(previous)) {
      return previous.replace(LINE_TEXT_TAIL, inline + '</p></div></div>');
    }
    if (LINE_PICTURE.test(previous) && LINE_PICTURE_TAIL.test(previous)) {
      return previous.replace(LINE_PICTURE_TAIL, inline + '</div></div>');
    }
    return null;
  }

  // Emits N rows per section: consecutive non-columns elements stack into one row;
  // each `columns` element becomes its own multi-column row, in document order.
  function renderSection(section, startRowIndex) {
    if (!section || !Array.isArray(section.elements) || section.elements.length === 0) {
      return { html: '', rowCount: 0 };
    }

    var isFullWidth = !!section.fullWidth;
    var elements = section.elements;
    var html = '';
    var rowIndex = startRowIndex;
    var buffer = [];

    function flushBuffer() {
      if (buffer.length === 0) return;
      var singleCol = wrapColumn(buffer, 1);
      html += wrapRow(singleCol, rowIndex++, 1, isFullWidth);
      buffer = [];
    }

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el) continue;
      if (el.type === 'columns') {
        flushBuffer();
        var columnsRow = renderColumnsRow(el, rowIndex, isFullWidth);
        if (columnsRow) { html += columnsRow; rowIndex++; }
      } else {
        var joined = buffer.length ? joinLegacyLine(buffer[buffer.length - 1], el) : null;
        if (joined !== null) { buffer[buffer.length - 1] = joined; continue; }
        var rendered = renderElement(el);
        if (rendered) buffer.push(wrapContent(rendered, buffer.length + 1));
      }
    }
    flushBuffer();

    return { html: html, rowCount: rowIndex - startRowIndex };
  }

  function render(data) {
    if (!hasNeoContent(data)) return '';
    var neo = data.contentBuilderNeoJson;

    var html = '';
    var nextRowIndex = 1;
    for (var i = 0; i < neo.sections.length; i++) {
      var result = renderSection(neo.sections[i], nextRowIndex);
      html += result.html;
      nextRowIndex += result.rowCount;
    }
    return html;
  }

  // ============================================================
  // Self-contained styles for slideshow/video/youtube/gallery.
  // Injected once per page so the renderer ships its own CSS.
  // ============================================================

  var STYLES_INJECTED = false;
  var NEO_STYLES = [
    '.neo-slideshow__viewport{position:relative;overflow:hidden;border-radius:inherit;}',
    '.neo-slideshow__track{display:flex;width:100%;height:100%;overflow-x:auto;overflow-y:hidden;scroll-snap-type:x mandatory;scroll-behavior:smooth;scrollbar-width:none;-ms-overflow-style:none;}',
    '.neo-slideshow__track::-webkit-scrollbar{display:none;}',
    '.neo-slideshow__slide{flex:0 0 100%;height:100%;scroll-snap-align:start;position:relative;}',
    '.neo-slideshow__slide img{width:100%;height:100%;object-fit:cover;display:block;}',
    '.neo-slideshow__nav{position:absolute;top:50%;transform:translateY(-50%);background:rgba(0,0,0,0.5);color:#fff;border:0;width:36px;height:36px;border-radius:50%;cursor:pointer;z-index:1;font-size:16px;line-height:1;padding:0;display:flex;align-items:center;justify-content:center;}',
    '.neo-slideshow__nav--prev{left:8px;}',
    '.neo-slideshow__nav--next{right:8px;}',
    '.neo-slideshow__dots{display:flex;justify-content:center;gap:8px;margin-top:8px;}',
    '.neo-slideshow__dot{width:8px;height:8px;border-radius:50%;border:0;background:#ccc;cursor:pointer;padding:0;}',
    '.neo-slideshow__dot.is-active{background:#333;}',
    '.neo-slideshow__caption{position:absolute;inset:0;padding:24px;background:rgba(0,0,0,0.35);color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;gap:12px;}',
    '.neo-slideshow__headline{margin:0;color:#fff !important;font-size:28px;font-weight:600;line-height:1.2;}',
    '.neo-slideshow__btn{display:inline-block;padding:8px 16px;background:#fff;color:#111;text-decoration:none;border-radius:4px;}',
    // The legacy .image-container keeps its inline <img> so the caption sits where news.css expects it.
    '.content-type-neo-image:not(.image-container) img{max-width:100%;height:auto;display:block;}',
    // overflow:hidden so wrapper border-radius actually clips the iframe/slides inside.
    '.content-type-neo-youtube,.content-type-neo-video,.content-type-neo-slideshow{overflow:hidden;}',
    '.content-type-neo-gallery{display:grid;gap:8px;}',
    '.content-type-neo-gallery img{width:100%;height:auto;display:block;}',
    '.neo-media-gallery__title{margin:0 0 16px;}',
    '.neo-media-gallery__grid{display:grid;gap:8px;}',
    '.neo-media-gallery__item{margin:0;}',
    '.neo-media-gallery__item img{width:100%;height:auto;display:block;}',
    '.neo-media-gallery__caption{font-size:12px;color:#555;margin-top:4px;}',
    '.neo-media-gallery__masonry{column-gap:8px;}',
    '.neo-media-gallery__masonry .neo-media-gallery__item{break-inside:avoid;margin:0 0 8px;display:block;}',
  ].join('');

  function injectStyles() {
    if (STYLES_INJECTED) return;
    STYLES_INJECTED = true;
    try {
      var style = document.createElement('style');
      style.setAttribute('data-neo-styles', '1');
      style.appendChild(document.createTextNode(NEO_STYLES));
      (document.head || document.documentElement).appendChild(style);
    } catch (e) { /* no-op */ }
  }

  // ============================================================
  // Hydration (runs after the HTML string has been injected)
  // ============================================================

  function hydrateSlideshow(root) {
    if (root.__neoWired) return;
    root.__neoWired = true;

    var track = root.querySelector('.neo-slideshow__track');
    if (!track) return;
    var slides = track.children;
    var count = slides.length;
    if (count === 0) return;

    var dots = root.querySelectorAll('.neo-slideshow__dot');
    var prev = root.querySelector('.neo-slideshow__nav--prev');
    var next = root.querySelector('.neo-slideshow__nav--next');
    var idx = 0;
    var timer = null;

    function updateDots() {
      for (var k = 0; k < dots.length; k++) {
        if (k === idx) dots[k].classList.add('is-active');
        else dots[k].classList.remove('is-active');
      }
    }

    function goTo(i) {
      if (i < 0) i = count - 1;
      if (i >= count) i = 0;
      idx = i;
      track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
      updateDots();
    }

    function resetAutoplay() {
      if (timer) { clearInterval(timer); timer = null; }
      if (root.getAttribute('data-autoplay') !== '1' || count < 2) return;
      var ms = parseInt(root.getAttribute('data-interval'), 10) || 5000;
      timer = setInterval(function () {
        if (!root.isConnected) { clearInterval(timer); timer = null; return; }
        goTo(idx + 1);
      }, ms);
    }

    if (prev) prev.addEventListener('click', function () { goTo(idx - 1); resetAutoplay(); });
    if (next) next.addEventListener('click', function () { goTo(idx + 1); resetAutoplay(); });
    for (var k = 0; k < dots.length; k++) {
      (function (k) {
        dots[k].addEventListener('click', function () { goTo(k); resetAutoplay(); });
      })(k);
    }

    // Track manual swipes so dots stay in sync.
    var scrollTick;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTick);
      scrollTick = setTimeout(function () {
        var w = track.clientWidth;
        if (w === 0) return;
        var i = Math.round(track.scrollLeft / w);
        if (i !== idx) { idx = i; updateDots(); }
      }, 80);
    });

    updateDots();
    resetAutoplay();
  }

  // ============================================================
  // Lightbox: click a media-gallery image → full-size singleton overlay.
  // ============================================================

  var lightbox = { el: null, imgEl: null, capEl: null, prevBtn: null, nextBtn: null, items: [], idx: 0 };

  function ensureLightbox() {
    if (lightbox.el) return;
    var el = document.createElement('div');
    el.setAttribute('data-neo-lightbox', '1');
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = 'position:fixed;inset:0;z-index:999999;background:rgba(0,0,0,0.9);display:none;align-items:center;justify-content:center;padding:40px;';
    el.innerHTML =
      '<button type="button" data-neo-lb-close aria-label="Close" style="position:absolute;top:16px;right:16px;background:rgba(0,0,0,0.5);border:0;color:#fff;width:40px;height:40px;border-radius:50%;cursor:pointer;font-size:22px;line-height:1;padding:0;display:flex;align-items:center;justify-content:center;z-index:2;">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
      '</button>' +
      '<button type="button" data-neo-lb-prev aria-label="Previous" style="position:absolute;top:50%;left:16px;transform:translateY(-50%);background:rgba(0,0,0,0.5);border:0;color:#fff;width:48px;height:48px;border-radius:50%;cursor:pointer;font-size:20px;display:flex;align-items:center;justify-content:center;z-index:1;">&#10094;</button>' +
      '<button type="button" data-neo-lb-next aria-label="Next" style="position:absolute;top:50%;right:16px;transform:translateY(-50%);background:rgba(0,0,0,0.5);border:0;color:#fff;width:48px;height:48px;border-radius:50%;cursor:pointer;font-size:20px;display:flex;align-items:center;justify-content:center;z-index:1;">&#10095;</button>' +
      '<div data-neo-lb-stage style="max-width:90vw;max-height:90vh;display:flex;flex-direction:column;align-items:center;gap:12px;">' +
        '<img data-neo-lb-img alt="" style="max-width:100%;max-height:80vh;object-fit:contain;display:block;">' +
        '<div data-neo-lb-cap style="color:#fff;text-align:center;font-size:14px;display:none;"></div>' +
      '</div>';
    document.body.appendChild(el);

    lightbox.el = el;
    lightbox.imgEl = el.querySelector('[data-neo-lb-img]');
    lightbox.capEl = el.querySelector('[data-neo-lb-cap]');
    lightbox.prevBtn = el.querySelector('[data-neo-lb-prev]');
    lightbox.nextBtn = el.querySelector('[data-neo-lb-next]');

    el.querySelector('[data-neo-lb-close]').addEventListener('click', closeLightbox);
    lightbox.prevBtn.addEventListener('click', function () { stepLightbox(-1); });
    lightbox.nextBtn.addEventListener('click', function () { stepLightbox(1); });
    el.addEventListener('click', function (e) { if (e.target === el) closeLightbox(); });
    document.addEventListener('keydown', function (e) {
      if (!lightbox.el || lightbox.el.style.display !== 'flex') return;
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') stepLightbox(-1);
      else if (e.key === 'ArrowRight') stepLightbox(1);
    });
  }

  function showLightboxItem() {
    var item = lightbox.items[lightbox.idx];
    if (!item) return;
    lightbox.imgEl.src = item.src;
    lightbox.imgEl.alt = item.alt || '';
    if (item.caption) {
      lightbox.capEl.textContent = item.caption;
      lightbox.capEl.style.display = 'block';
    } else {
      lightbox.capEl.textContent = '';
      lightbox.capEl.style.display = 'none';
    }
    var many = lightbox.items.length > 1;
    lightbox.prevBtn.style.display = many ? 'flex' : 'none';
    lightbox.nextBtn.style.display = many ? 'flex' : 'none';
  }

  function openLightbox(items, idx) {
    if (!items || items.length === 0) return;
    ensureLightbox();
    lightbox.items = items;
    lightbox.idx = Math.max(0, Math.min(items.length - 1, idx || 0));
    showLightboxItem();
    lightbox.el.style.display = 'flex';
    lightbox.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    if (!lightbox.el) return;
    lightbox.el.style.display = 'none';
    lightbox.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    lightbox.items = [];
    lightbox.idx = 0;
  }

  function stepLightbox(dir) {
    if (lightbox.items.length === 0) return;
    var n = lightbox.items.length;
    lightbox.idx = (lightbox.idx + dir + n) % n;
    showLightboxItem();
  }

  function hydrateMediaGalleryLightbox(root) {
    if (root.__neoLbWired) return;
    root.__neoLbWired = true;

    var imgs = root.querySelectorAll('.neo-media-gallery__item img');
    for (var i = 0; i < imgs.length; i++) imgs[i].style.cursor = 'zoom-in';

    root.addEventListener('click', function (e) {
      var img = e.target && e.target.tagName === 'IMG' ? e.target : null;
      if (!img || !img.closest('.neo-media-gallery__item')) return;
      var all = root.querySelectorAll('.neo-media-gallery__item img');
      var items = [];
      var startIdx = 0;
      for (var k = 0; k < all.length; k++) {
        var fig = all[k].closest('.neo-media-gallery__item');
        var capNode = fig ? fig.querySelector('.neo-media-gallery__caption') : null;
        items.push({
          src: all[k].getAttribute('src') || all[k].src,
          alt: all[k].getAttribute('alt') || '',
          caption: capNode ? capNode.textContent.trim() : ''
        });
        if (all[k] === img) startIdx = k;
      }
      openLightbox(items, startIdx);
    });
  }

  // Same options news.js uses for its own .slider-for / .slider-nav, applied per
  // album so a page with several albums does not cross-link their navs. news.js
  // later destroys and re-inits every .slider-for/.slider-nav globally; the
  // slick-initialized guard keeps this from stacking a second instance.
  function hydrateLegacyAlbum(root) {
    var $ = window.jQuery;
    if (!$ || !$.fn || typeof $.fn.slick !== 'function') return;
    var $for = $(root).find('.slider-for');
    var $nav = $(root).find('.slider-nav');
    if (!$for.length || !$nav.length) return;
    if ($for.hasClass('slick-initialized') || $nav.hasClass('slick-initialized')) return;
    var slidesToShow = $nav.children().length > 9 ? 9 : 7;
    $for.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      infinite: false,
      arrows: true,
      asNavFor: $nav,
      adaptiveHeight: true
    });
    $nav.slick({
      slidesToShow: slidesToShow,
      slidesToScroll: 1,
      asNavFor: $for,
      centerMode: true,
      focusOnSelect: true,
      infinite: false,
      responsive: [
        { breakpoint: 767, settings: { slidesToShow: 7 } },
        { breakpoint: 540, settings: { slidesToShow: 5 } }
      ]
    });
  }

  function hydrate(root) {
    injectStyles();
    var scope = root && root.querySelectorAll ? root : document;
    var albums = scope.querySelectorAll('.content-type-neo-media-gallery.content-type-content-album, .content-type-neo-carousel .content-type-neo-media-gallery');
    for (var a = 0; a < albums.length; a++) hydrateLegacyAlbum(albums[a]);
    var slideshows = scope.querySelectorAll('[data-neo-slideshow="1"]');
    for (var i = 0; i < slideshows.length; i++) hydrateSlideshow(slideshows[i]);
    var galleries = scope.querySelectorAll('.content-type-neo-media-gallery');
    for (var k = 0; k < galleries.length; k++) hydrateMediaGalleryLightbox(galleries[k]);
  }

  // ============================================================
  // Public API
  // ============================================================

  window.NewsNeo = {
    hasContent: hasNeoContent,
    render: render,
    hydrate: hydrate,
    registerType: function (type, fn) {
      if (typeof type === 'string' && typeof fn === 'function') {
        renderers[type] = fn;
      }
    },
    _renderers: renderers
  };

})(window);
